/**
 * 页面组子页面选择规则（与线上 t_page 2026-06-16 导出数据对齐）。
 *
 * 已证实事实：
 * - 3 个页面组、最大深度 2（页面组 → 主信息流）、无环、无嵌套页面组、子页面均为「主信息流」
 *
 * 前端约束目的：
 * - 运营只能选「主信息流」叶子页，天然限制为两层，避免环状引用与混合类型。
 * - 与后端 PageResourceComponentValidator / App 下发（item type 固定 201）保持一致。
 */

export const PAGE_TYPE_PAGE_GROUP = "页面组";
export const PAGE_TYPE_MAIN_STREAM = "主信息流";

/** 页面组 → 子页面：线上最深为 2 层（组 + 叶子页） */
export const PAGE_GROUP_MAX_DEPTH = 2;

export function isMainInfoStreamPage(page) {
  return Boolean(page && page.type === PAGE_TYPE_MAIN_STREAM);
}

export function isPageGroupPage(page) {
  return Boolean(page && page.type === PAGE_TYPE_PAGE_GROUP);
}

export function normalizeResourceIdList(resourceId) {
  if (resourceId === null || resourceId === undefined || resourceId === "") {
    return [];
  }
  if (Array.isArray(resourceId)) {
    return resourceId
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id) && id > 0);
  }
  const single = Number(resourceId);
  return Number.isNaN(single) || single <= 0 ? [] : [single];
}

/**
 * 页面组「选择页面」下拉候选：仅主信息流，排除当前编辑页（防自引用）。
 *
 * @param {Array} allPages
 * @param {{ currentPageId?: number|string|null }} [options]
 * @returns {Array}
 */
export function filterPageGroupChildOptions(allPages, options = {}) {
  const currentPageId =
    options.currentPageId === null || options.currentPageId === undefined
      ? null
      : Number(options.currentPageId);
  return (allPages || []).filter((page) => {
    if (!isMainInfoStreamPage(page)) {
      return false;
    }
    if (
      currentPageId !== null &&
      !Number.isNaN(currentPageId) &&
      Number(page.id) === currentPageId
    ) {
      return false;
    }
    return true;
  });
}

/**
 * 保存前校验页面组组件绑定的子页面。
 *
 * @param {Array} components
 * @param {{ currentPageId?: number|string|null, allPages: Array }} context
 * @returns {{ valid: boolean, message?: string }}
 */
export function validatePageGroupComponents(components, context) {
  const allPages = context.allPages || [];
  const pageById = new Map(allPages.map((page) => [Number(page.id), page]));
  const currentPageId =
    context.currentPageId === null || context.currentPageId === undefined
      ? null
      : Number(context.currentPageId);

  for (let i = 0; i < (components || []).length; i += 1) {
    const component = components[i];
    const ids = normalizeResourceIdList(component && component.resourceId);
    if (!ids.length) {
      return {
        valid: false,
        message: `第 ${i + 1} 个组件：请选择主信息流子页面`,
      };
    }
    for (const childId of ids) {
      if (
        currentPageId !== null &&
        !Number.isNaN(currentPageId) &&
        childId === currentPageId
      ) {
        return {
          valid: false,
          message: `第 ${i + 1} 个组件：不能选择当前页面自身（会形成环）`,
        };
      }
      const childPage = pageById.get(childId);
      if (!childPage) {
        return {
          valid: false,
          message: `第 ${i + 1} 个组件：子页面不存在或已下线`,
        };
      }
      if (!isMainInfoStreamPage(childPage)) {
        return {
          valid: false,
          message: `第 ${i + 1} 个组件：仅允许关联「主信息流」页面（当前选中为「${childPage.type}」）`,
        };
      }
    }
  }
  return { valid: true };
}

/**
 * 选中变更时即时校验；不通过则返回应回退的 resourceId。
 *
 * @param {number|number[]|string} nextResourceId
 * @param {number|number[]|string} previousResourceId
 * @param {{ currentPageId?: number|string|null, allPages: Array }} context
 * @returns {{ ok: boolean, message?: string, resourceId: * }}
 */
export function guardPageGroupResourceChange(
  nextResourceId,
  previousResourceId,
  context
) {
  const probe = [{ resourceId: nextResourceId }];
  const result = validatePageGroupComponents(probe, context);
  if (!result.valid) {
    return {
      ok: false,
      message: result.message,
      resourceId: previousResourceId,
    };
  }
  return { ok: true, resourceId: nextResourceId };
}
